import RestEndpointsEnum from "../models/RestEndpointsEnum";
import { buildRestUrlWithQuery, buildAuthHeadersForFetch } from "./RestService";
import CatalogItemService from "./CatalogItemService";
import IUserUI from "../models/IUserUI";
import ISupplierModel from "../models/ISupplierModel";
import IVendorModel from "../models/IVendorModel";
import ICatalog_Item from "../models/ICatalog_Item";
import IOrder_Item from "../models/IOrder_Item";
import ReportPeriodType from "../models/ReportPeriodTypeEnum";
import IOrderEntryOnGetAccountOrderEntryItemsDTO from "../models/DTO/IOrderEntryOnGetAccountOrderEntryItemsDTO";
import ITypeaheadCatalogItemRestDTO from "../models/DTO/ITypeaheadCatalogItemRestDTO";

/** Cap of the catalog typeahead's exact-lookup branch (CatalogItemService.MAX_EXACT_LOOKUP_IDS). */
export const EXACT_LOOKUP_MAX_IDS = 200;
/** The account-items endpoint's page cap we ask for: one row per distinct item, newest first. */
export const RECENT_ITEMS_LIMIT = 200;

export interface IRecentItemsQuery {
    catalogGuid: string;
    priceGroupGuid: string;
    periodType?: ReportPeriodType;
    limit?: number;
    offset?: number;
}

/** A recently ordered catalog item hydrated into the storefront card shape. */
export interface IRecentFavoriteCandidate {
    catalogItem: ICatalog_Item;
    /** Quantity on the customer's most recent order line for the item. */
    lastQty: number;
}

/**
 * The REST body DTO the API syncs over omits catalogItemIds, but the controller spreads the whole
 * body into the socket DTO, so the exact-lookup branch is reachable over REST too.
 */
type ExactLookupOptions = Omit<ITypeaheadCatalogItemRestDTO, "name"> & {
    catalogItemIds: number[];
    excludeHiddenCategoryItems?: boolean;
};

/**
 * GET /v1/order-entry/account-items — the customer's recently ordered items (one row per distinct
 * catalog item, most recent line first). Vendor-scoped by the vendorGuid header, unlike the
 * supplier-only socket twin used by the order-entry sidebar.
 *
 * The Catalog_Item on each row is attribute-limited (no guid, no attachments), so callers that
 * need the card shape hydrate through lookupCatalogItemsByIds.
 */
export const getRecentAccountItems = async (
    query: IRecentItemsQuery,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel
): Promise<IOrderEntryOnGetAccountOrderEntryItemsDTO> => {
    const url = buildRestUrlWithQuery({
        endpoint: RestEndpointsEnum.OrderEntryGetAccountItems,
        query: {
            periodType: query.periodType ?? ReportPeriodType.LAST_SIX_MONTHS,
            catalogGuid: query.catalogGuid,
            priceGroupGuid: query.priceGroupGuid,
            limit: query.limit ?? RECENT_ITEMS_LIMIT,
            offset: query.offset ?? 0,
        },
    });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel, vendorModel });

    const response = await fetch(url, { method: "GET", headers });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || response.statusText || "Failed to load recent items");
    }

    return (await response.json()) as IOrderEntryOnGetAccountOrderEntryItemsDTO;
};

/**
 * Exact lookup by catalog item ids through the catalog typeahead (the same branch the server's own
 * favorites list uses): applies active state, the price-group N/A guard, template and hidden-category
 * restrictions, and returns the full card shape with signed attachment urls. Chunked to the server cap.
 */
export const lookupCatalogItemsByIds = async (
    catalogGuid: string,
    catalogItemIds: number[],
    priceGroupGuid: string | undefined,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel
): Promise<ICatalog_Item[]> => {
    const uniqueIds = Array.from(new Set(catalogItemIds.filter((id) => Number.isFinite(id))));
    let items: ICatalog_Item[] = [];

    for (let start = 0; start < uniqueIds.length; start += EXACT_LOOKUP_MAX_IDS) {
        const options: ExactLookupOptions = {
            catalogItemIds: uniqueIds.slice(start, start + EXACT_LOOKUP_MAX_IDS),
            onlyActive: true,
            includeKitItems: true,
            excludeHiddenCategoryItems: true,
            priceGroupGuid,
        };

        const result = await CatalogItemService.typeaheadCatalogItems(catalogGuid, "", userModel, supplierModel, vendorModel, options);

        items = items.concat(result.catalog_items || []);
    }

    return items;
};

/**
 * Recently ordered items (last six months) as favorite candidates: hydrated card-shaped items in
 * most-recently-ordered order, minus product-group parents (favorites are per SKU) and minus
 * anything the customer can no longer see (filtered by the lookup).
 */
export const getRecentItemsForFavorites = async (
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel: IVendorModel
): Promise<IRecentFavoriteCandidate[]> => {
    const catalogGuid = vendorModel.Catalog?.guid;
    const priceGroupGuid = vendorModel.Price_Group?.guid;

    if (!catalogGuid || !priceGroupGuid) {
        return [];
    }

    const recent = await getRecentAccountItems({ catalogGuid, priceGroupGuid }, userModel, supplierModel, vendorModel);
    const orderItems: IOrder_Item[] = recent.items || [];

    // Most recent line per catalog item id, preserving the endpoint's (newest-first) order.
    const lastQtyById = new Map<number, number>();

    for (const orderItem of orderItems) {
        const catalogItemId = orderItem.catalog_item_id ?? orderItem.Catalog_Item?.id;

        if (catalogItemId && !lastQtyById.has(catalogItemId)) {
            lastQtyById.set(catalogItemId, Number(orderItem.qty) || 0);
        }
    }

    if (!lastQtyById.size) {
        return [];
    }

    const catalogItems = await lookupCatalogItemsByIds(
        catalogGuid,
        Array.from(lastQtyById.keys()),
        priceGroupGuid,
        userModel,
        supplierModel,
        vendorModel
    );

    const byId = new Map<number, ICatalog_Item>(catalogItems.map((item) => [item.id, item]));
    const candidates: IRecentFavoriteCandidate[] = [];

    for (const [catalogItemId, lastQty] of lastQtyById) {
        const catalogItem = byId.get(catalogItemId);

        if (catalogItem && catalogItem.guid && !catalogItem.is_product_group) {
            candidates.push({ catalogItem, lastQty });
        }
    }

    return candidates;
};

const RecentItemsService = {
    getRecentAccountItems,
    lookupCatalogItemsByIds,
    getRecentItemsForFavorites,
};

export default RecentItemsService;
